import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  Package, 
  Briefcase, 
  Sofa, 
  Bike,
  Box,
  Bed,
  Monitor,
  Calendar,
  CheckCircle2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  boxes: Box,
  luggage: Briefcase,
  plastics: Package,
  furniture: Monitor,
  office: Briefcase,
  couches: Sofa,
  bedroom: Bed,
  sports: Bike,
  miscellaneous: Package
};

const categoryColors = {
  boxes: "bg-blue-100 text-blue-700",
  luggage: "bg-purple-100 text-purple-700",
  plastics: "bg-green-100 text-green-700",
  furniture: "bg-yellow-100 text-yellow-700",
  office: "bg-cyan-100 text-cyan-700",
  couches: "bg-orange-100 text-orange-700",
  bedroom: "bg-pink-100 text-pink-700",
  sports: "bg-emerald-100 text-emerald-700",
  miscellaneous: "bg-gray-100 text-gray-700"
};

export const Pricing = () => {
  const [pricing, setPricing] = useState({});
  const [activeCategory, setActiveCategory] = useState('boxes');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        setPricing(response.data);
      } catch (error) {
        console.error('Error fetching pricing:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const categories = Object.keys(pricing);

  return (
    <div data-testid="pricing-page">
      {/* Hero Section - White with accent */}
      <section className="bg-white section-padding border-b border-gray-100" data-testid="pricing-hero">
        <div className="section-container">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-brand mb-4 block">
              Transparent Pricing
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-navy mb-6 leading-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Our Pricing
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Simple monthly pricing per item. No hidden fees. $30 reservation deposit to secure your spot.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="px-6 py-3 bg-navy/5 border border-navy/10 rounded-full text-navy">
                <CheckCircle2 className="w-5 h-5 inline-block mr-2 text-green-600" />
                $30 Reservation Deposit
              </div>
              <div className="px-6 py-3 bg-navy/5 border border-navy/10 rounded-full text-navy">
                <CheckCircle2 className="w-5 h-5 inline-block mr-2 text-green-600" />
                Monthly Billing
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Content */}
      <section className="section-padding bg-surface" data-testid="pricing-content">
        <div className="section-container">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-navy border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading pricing...</p>
            </div>
          ) : (
            <>
              {/* Category Tabs */}
              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {categories.map((category) => {
                  const Icon = categoryIcons[category] || Package;
                  return (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={`px-4 py-2 rounded-full font-medium text-sm transition-all flex items-center gap-2 ${
                        activeCategory === category
                          ? 'bg-navy text-white shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                      data-testid={`category-${category}`}
                    >
                      <Icon className="w-4 h-4" />
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  );
                })}
              </div>

              {/* Active Category Items */}
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-4xl mx-auto"
              >
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className={`p-6 ${categoryColors[activeCategory] || 'bg-gray-100'}`}>
                    <h2 
                      className="text-2xl font-bold capitalize flex items-center gap-3"
                      style={{ fontFamily: 'Chivo, sans-serif' }}
                    >
                      {(() => {
                        const Icon = categoryIcons[activeCategory] || Package;
                        return <Icon className="w-8 h-8" />;
                      })()}
                      {activeCategory}
                    </h2>
                    <p className="text-sm mt-1 opacity-80">Monthly storage rates</p>
                  </div>
                  
                  <div className="divide-y divide-gray-100">
                    {pricing[activeCategory]?.map((item, index) => (
                      <motion.div
                        key={item.name}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <span className="text-gray-800 font-medium">{item.name}</span>
                        <span className="text-navy font-bold text-lg">
                          ${item.price.toFixed(2)}<span className="text-gray-400 text-sm font-normal">/mo</span>
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* All Categories Grid */}
              <div className="mt-16">
                <h3 
                  className="text-2xl font-bold text-navy text-center mb-8"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  Quick Reference - All Categories
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categories.map((category) => {
                    const Icon = categoryIcons[category] || Package;
                    const items = pricing[category] || [];
                    const minPrice = Math.min(...items.map(i => i.price));
                    const maxPrice = Math.max(...items.map(i => i.price));
                    
                    return (
                      <motion.div
                        key={category}
                        className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => setActiveCategory(category)}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className={`w-12 h-12 rounded-lg ${categoryColors[category]} flex items-center justify-center mb-4`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <h4 className="font-bold text-navy capitalize mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          {category}
                        </h4>
                        <p className="text-sm text-gray-500 mb-2">{items.length} items</p>
                        <p className="text-navy font-bold">
                          ${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}<span className="text-gray-400 text-sm font-normal">/mo</span>
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Special Note */}
              <div className="mt-12 bg-white rounded-xl p-6 border-l-4 border-red-brand">
                <h4 className="font-bold text-navy mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Out of Box Items
                </h4>
                <p className="text-gray-600">
                  For items not listed above, we charge <span className="font-bold text-navy">$5 per cubic foot</span> per month. 
                  Contact us for a custom quote.
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gray-50 border-t border-gray-100" data-testid="pricing-cta">
        <div className="section-container text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-navy mb-6"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Ready to Reserve?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Secure your storage with a $30 deposit. Pay the remaining balance after pickup.
          </p>
          <Link to="/contact">
            <Button className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide text-base px-8 py-4 rounded-lg shadow-lg" data-testid="pricing-reserve-btn">
              <Calendar className="w-5 h-5 mr-2" />
              Reserve Storage Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
